/**
 * PCM16 <-> Float32 conversion for a GPT-Live voice call.
 *
 * GPT-Live's WebSocket audio is raw, headerless, mono, signed 16-bit
 * little-endian PCM at 24 kHz, base64-encoded. Web Audio works in Float32, so
 * both directions convert here; the AudioContext is constructed at
 * {@link AUDIO_SAMPLE_RATE} so no resampling is needed.
 */

export const AUDIO_SAMPLE_RATE = 24_000;

/** Full-scale for signed 16-bit. Positive and negative peaks scale by the same
 * factor, which clips -1.0 to -32767 rather than -32768 — inaudible, and it keeps
 * the round trip symmetric. */
const PCM16_PEAK = 0x7fff;

/** Chunk the base64 conversion so a long utterance cannot blow the argument limit
 * of `String.fromCharCode` (~64k arguments in practice). */
const BASE64_CHUNK = 0x8000;

/** Float32 samples in [-1, 1] to base64-encoded little-endian PCM16. */
export function encodePcm16(samples: Float32Array): string {
  const buffer = new ArrayBuffer(samples.length * 2);
  const view = new DataView(buffer);
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index] ?? 0));
    view.setInt16(index * 2, Math.round(sample * PCM16_PEAK), true);
  }
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK) {
    binary += String.fromCharCode(
      ...bytes.subarray(offset, offset + BASE64_CHUNK),
    );
  }
  return btoa(binary);
}

/**
 * Base64-encoded little-endian PCM16 back to Float32 samples in [-1, 1].
 *
 * The `<ArrayBuffer>` argument is not decoration: bare `Float32Array` widens to
 * `ArrayBufferLike`, which `AudioBuffer.copyToChannel` refuses because it cannot
 * accept a `SharedArrayBuffer`-backed view.
 */
export function decodePcm16(base64: string): Float32Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  // A truncated frame would otherwise read one byte past the end as a whole sample.
  const sampleCount = Math.floor(bytes.length / 2);
  const view = new DataView(bytes.buffer, bytes.byteOffset, sampleCount * 2);
  const samples = new Float32Array(sampleCount);
  for (let index = 0; index < sampleCount; index += 1) {
    samples[index] = view.getInt16(index * 2, true) / PCM16_PEAK;
  }
  return samples;
}

/** RMS above this (about -40 dBFS) counts as someone talking. On the mic,
 * noise suppression and echo cancellation run first, so room hum and the
 * assistant's own voice mostly fall below it. */
const VOICE_RMS = 0.01;

/** Whether a frame of samples is louder than background or silence. */
export function hasVoice(samples: Float32Array): boolean {
  let sum = 0;
  for (const sample of samples) sum += sample * sample;
  return samples.length > 0 && Math.sqrt(sum / samples.length) > VOICE_RMS;
}
